import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { ContactPartner, ContactPartnerFormData } from '@/types';
import { Edit, Mail, Phone, Plus, Trash2, User } from 'lucide-react';
import { useState } from 'react';
import ContactPartnerDialog from './ContactPartnerDialog';

interface ContactPartnerManagerProps {
  contactPartners: ContactPartner[];
  onAdd: (data: ContactPartnerFormData) => void;
  onEdit: (id: string, data: ContactPartnerFormData) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
}

/**
 * Contact Partner Manager Component
 * Handles listing, adding, editing, and deleting contact partners
 * @param contactPartners - Array of contact partners to display
 * @param onAdd - Callback when adding a new contact partner
 * @param onEdit - Callback when editing an existing contact partner
 * @param onDelete - Callback when deleting a contact partner
 * @param isLoading - Loading state for operations
 */
export default function ContactPartnerManager({
  contactPartners,
  onAdd,
  onEdit,
  onDelete,
  isLoading = false,
}: ContactPartnerManagerProps) {
  // Translation hook
  const { t: tForms } = useFormsTranslation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<
    ContactPartner | undefined
  >();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState<
    ContactPartner | undefined
  >();

  const handleAddClick = () => {
    setEditingPartner(undefined);
    setDialogOpen(true);
  };

  const handleEditClick = (partner: ContactPartner) => {
    setEditingPartner(partner);
    setDialogOpen(true);
  };

  const handleDeleteClick = (partner: ContactPartner) => {
    setPartnerToDelete(partner);
    setDeleteDialogOpen(true);
  };

  const handleSave = (data: ContactPartnerFormData) => {
    if (editingPartner) {
      onEdit(editingPartner.id, data);
    } else {
      onAdd(data);
    }
    setDialogOpen(false);
    setEditingPartner(undefined);
  };

  const confirmDelete = () => {
    if (partnerToDelete) {
      onDelete(partnerToDelete.id);
      setDeleteDialogOpen(false);
      setPartnerToDelete(undefined);
    }
  };

  const formatContactType = (contactType: string) => {
    return tForms(`contactPartner.contactTypes.${contactType}`) || contactType;
  };

  const formatLanguage = (language: string) => {
    return tForms(`contactPartner.languages.${language}`) || language;
  };

  return (
    <div className='space-y-6'>
      {/* Header with Add Button */}
      <div className='flex items-center justify-between'>
        <div>
          <h3 className='text-xl font-semibold'>
            {tForms('contactPartner.title')}
          </h3>
          <p className='text-sm text-muted-foreground'>
            {tForms('contactPartner.description')}
          </p>
        </div>
        <Button
          onClick={handleAddClick}
          disabled={isLoading}
          className='flex items-center gap-2'
        >
          <Plus className='h-4 w-4' />
          {tForms('contactPartner.addButton')}
        </Button>
      </div>

      {/* Contact Partners Table */}
      {contactPartners.length > 0 ? (
        <div className='rounded-md border'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {tForms('contactPartner.tableHeaders.name')}
                </TableHead>
                <TableHead>
                  {tForms('contactPartner.tableHeaders.type')}
                </TableHead>
                <TableHead>
                  {tForms('contactPartner.tableHeaders.function')}
                </TableHead>
                <TableHead>
                  {tForms('contactPartner.tableHeaders.language')}
                </TableHead>
                <TableHead>
                  {tForms('contactPartner.tableHeaders.status')}
                </TableHead>
                <TableHead>
                  {tForms('contactPartner.tableHeaders.contact')}
                </TableHead>
                <TableHead className='text-right'>
                  {tForms('contactPartner.tableHeaders.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contactPartners.map(partner => (
                <TableRow key={partner.id}>
                  <TableCell>
                    <div className='flex items-center gap-2'>
                      <User className='h-4 w-4 text-muted-foreground' />
                      <div>
                        <div className='font-medium'>
                          {partner.firstName} {partner.lastName}
                        </div>
                        {partner.title && (
                          <div className='text-sm text-muted-foreground'>
                            {partner.title}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant='secondary'>
                      {formatContactType(partner.contactType)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className='text-sm'>{partner.function || '-'}</div>
                  </TableCell>
                  <TableCell>{formatLanguage(partner.language)}</TableCell>
                  <TableCell>
                    <div className='flex flex-col gap-1'>
                      {partner.isRepresentative && (
                        <Badge variant='outline' className='w-fit'>
                          {tForms('contactPartner.representative')}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className='space-y-1'>
                      <div className='flex items-center gap-2'>
                        <Mail className='h-3 w-3 text-muted-foreground' />
                        <span className='text-sm'>{partner.email}</span>
                      </div>
                      {partner.phone && (
                        <div className='flex items-center gap-2'>
                          <Phone className='h-3 w-3 text-muted-foreground' />
                          <span className='text-sm'>{partner.phone}</span>
                        </div>
                      )}
                      {partner.mobile && !partner.phone && (
                        <div className='flex items-center gap-2'>
                          <Phone className='h-3 w-3 text-muted-foreground' />
                          <span className='text-sm'>{partner.mobile}</span>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className='text-right'>
                    <div className='flex items-center justify-end gap-2'>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => handleEditClick(partner)}
                        disabled={isLoading}
                        aria-label={`Edit ${partner.firstName} ${partner.lastName}`}
                      >
                        <Edit className='h-4 w-4' />
                      </Button>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => handleDeleteClick(partner)}
                        disabled={isLoading}
                        className='text-destructive hover:text-destructive'
                        aria-label={`Delete ${partner.firstName} ${partner.lastName}`}
                      >
                        <Trash2 className='h-4 w-4' />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className='text-center py-12 border rounded-lg border-dashed'>
          <User className='h-12 w-12 text-muted-foreground mx-auto mb-4' />
          <h3 className='text-lg font-medium mb-2'>
            {tForms('contactPartner.noPartnersTitle')}
          </h3>
          <p className='text-muted-foreground mb-4'>
            {tForms('contactPartner.noPartnersDescription')}
          </p>
          <Button onClick={handleAddClick} disabled={isLoading}>
            <Plus className='h-4 w-4 mr-2' />
            {tForms('contactPartner.addButton')}
          </Button>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <ContactPartnerDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        contactPartner={editingPartner}
        onSave={handleSave}
        isLoading={isLoading}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {tForms('contactPartner.deleteDialogTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {tForms('contactPartner.deleteDialogDescription', {
                name: `${partnerToDelete?.firstName} ${partnerToDelete?.lastName}`,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>
              {tForms('contactPartner.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isLoading}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              {tForms('contactPartner.confirmDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
